import { useCallback, useMemo, useState, useEffect } from 'react';
import { PostStatusBadge } from '../components/PostStatusBadge';
import { DepositStatusBadge } from '../components/DepositStatusBadge';
import { PostMethodBadge } from '../components/PostMethodBadge';
import { DepositGroupBand } from '../components/DepositGroupBand';
import { ReceiptBand } from '../components/ReceiptBand';
import {
  ArrowClockwise,
  CheckCircle,
  Clock,
  MagnifyingGlass,
  WarningCircle,
  XCircle,
} from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  fetchBankTransactionsThunk,
  fetchCashReceiptsThunk,
  fetchRemittanceRecordsThunk,
  retryDepositThunk,
  retryPostThunk,
} from '../../../../redux/services/cashApplicationService';
import type {
  BankTransactionRecord,
  CashReceipt,
  DepositStatus,
  RemittanceRecord,
} from '@dill/dill-shared';
import {
  depositUnaccountedAmount,
  isDepositAbandoned,
  recordUnappliedAmount,
  recordWasAdjusted,
} from '@dill/dill-shared';
import { DillButton } from '../../../general/Dill';
import { formatCurrency, formatDate } from '../utils';
import { ReceiptDetailModal } from './ReceiptDetailModal';
import { toast } from 'react-toastify';

/**
 * A receipt with the payments it covers already looked up.
 *
 * The receipt names its records and holds no per-payment detail of its own, so every column below
 * — payer, amount, invoices, confidence — is read from the records. `missingIds` is kept rather
 * than dropped: a record that has not loaded still counts towards what the receipt says it covers.
 */
interface ReceiptWithRecords {
  receipt: CashReceipt;
  records: RemittanceRecord[];
  missingIds: string[];
  visibleTotal: number;
  postedCount: number;
}

/**
 * The receipts one bank credit's deposit covers.
 *
 * Usually one — a credit's payments are approved together, which is what makes them one receipt.
 * Two when the credit was linked in more than one round, and then the deposit still belongs to the
 * credit and spans both, so it has one answer and it is shown here rather than on either receipt.
 */
interface CreditGroup {
  key: string;
  deposit: BankTransactionRecord | null;
  erpDepositId: string | null;
  entries: ReceiptWithRecords[];
  total: number;
  paymentCount: number;
  status: DepositStatus;
  statusTitle: string | null;
  recoverable: boolean;
  /** What the credit is still owed, which is why its deposit has not been made. */
  unaccounted: number;
}

/**
 * The credit's deposit status, derived rather than stored.
 *
 * Nothing writes a status for a credit covered by two receipts — each carries its own, and they
 * diverge while the deposit is being made: one holds the claim and reads PENDING, the other WAITING.
 * Answering worst-news-first is what makes the band say the useful thing, and it means the band
 * cannot go stale against the receipts it summarises.
 */
const creditDepositStatus = (receipts: CashReceipt[]): DepositStatus => {
  if (receipts.some((receipt) => receipt.depositStatus === 'FAILED')) {
    return 'FAILED';
  }
  if (receipts.some((receipt) => receipt.depositStatus === 'PENDING')) {
    return 'PENDING';
  }
  if (receipts.every((receipt) => receipt.depositStatus === 'SUCCESSFUL')) {
    return 'SUCCESSFUL';
  }
  if (receipts.every((receipt) => receipt.depositStatus === 'NOT_APPLICABLE')) {
    return 'NOT_APPLICABLE';
  }
  return 'WAITING';
};

/** How many rows to draw before asking. There is no virtualizer here. */
const PAGE_SIZE = 100;

export const CashApplicationPostHistory = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.cashApplication.postHistory);
  const bankTransactions = useAppSelector((state) => state.cashApplication.bankTransactions);
  const recordsById = useAppSelector((state) => state.cashApplication.remittanceRecordsById);
  const loading = useAppSelector((state) => state.cashApplication.loadingList.length > 0);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;
  const [search, setSearch] = useState('');
  /**
   * Which bands the operator has closed, rather than which are open — open is the default, so
   * tracking the open set would reopen every band the operator had tidied away each time the
   * receipts were re-fetched.
   */
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // The open receipt is held by id, not by value, so the modal re-reads from the store. Retrying
  // from inside it has to show the result, and a snapshot taken on click would freeze at Failed
  // while the re-fetch updated everything behind it.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (supplierId) {
      dispatch(fetchCashReceiptsThunk({ supplierId }));
      // The payments themselves. A receipt lists its records and carries none of their detail, so
      // without this every row below would be a placeholder. This tab is its own route, so nothing
      // else has fetched them.
      dispatch(fetchRemittanceRecordsThunk({ supplierId }));
      // The credits too: a deposit's band names the amount and date the bank credited, and neither
      // is on the receipt. Unfiltered on the server, so already-matched credits come back.
      dispatch(fetchBankTransactionsThunk({ supplierId }));
    }
  }, [dispatch, supplierId]);

  /**
   * Re-read all three, because a post's outcome is spread across all three.
   *
   * The receipt carries the roll-up, each record carries its own ERP result, and the deposit id
   * lives on the bank credit. Refreshing only the receipts would leave a partly-posted row showing
   * yesterday's per-payment detail under today's status.
   */
  const handleRefresh = useCallback(() => {
    if (!supplierId) return;
    dispatch(fetchCashReceiptsThunk({ supplierId }));
    dispatch(fetchRemittanceRecordsThunk({ supplierId }));
    dispatch(fetchBankTransactionsThunk({ supplierId }));
  }, [dispatch, supplierId]);

  const successfulCount = items.filter((i) => i.postStatus === 'SUCCESSFUL').length;
  const pendingCount = items.filter(
    (i) => i.postStatus === 'PENDING' || i.postStatus === 'POSTING'
  ).length;
  const partialCount = items.filter((i) => i.postStatus === 'PARTIAL').length;
  const failedCount = items.filter((i) => i.postStatus === 'FAILED').length;
  const totalAmount = items
    .filter((i) => i.postStatus === 'SUCCESSFUL')
    .reduce((sum, i) => sum + i.amount, 0);

  const detailItem = useMemo(
    () => items.find((item) => item.id === detailId) ?? null,
    [items, detailId]
  );

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return items;
    return items.filter((item) => item.postStatus === statusFilter);
  }, [items, statusFilter]);

  /** Every receipt with its payments attached, newest first. */
  const entries = useMemo<ReceiptWithRecords[]>(() => {
    const built = filteredItems.map((receipt) => {
      const records: RemittanceRecord[] = [];
      const missingIds: string[] = [];
      for (const id of receipt.remittanceIds) {
        const record = recordsById[id];
        if (record) {
          records.push(record);
        } else {
          missingIds.push(id);
        }
      }
      return {
        receipt,
        records,
        missingIds,
        visibleTotal: records.reduce((sum, record) => sum + record.amount, 0),
        postedCount: records.filter((record) => record.erpPostStatus === 'SUCCESSFUL').length,
      };
    });
    return built.sort(
      (a, b) =>
        new Date(b.receipt.postedAt ?? b.receipt.createdAt).getTime() -
        new Date(a.receipt.postedAt ?? a.receipt.createdAt).getTime()
    );
  }, [filteredItems, recordsById]);

  /**
   * Filter by payer, invoice or deposit id.
   *
   * A band whose only match is at receipt level — someone pasting an `erpDepositId` from QuickBooks
   * — keeps all of its rows. Narrowing them to nothing would answer a search for a deposit with an
   * empty deposit.
   */
  const visibleEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return entries;
    }
    return entries
      .map((entry) => {
        if ((entry.receipt.erpDepositId ?? '').toLowerCase().includes(query)) {
          return entry;
        }
        const records = entry.records.filter(
          (record) =>
            (record.payerName ?? '').toLowerCase().includes(query) ||
            (record.checkNumber ?? '').toLowerCase().includes(query) ||
            record.appliedInvoices.some((allocation) =>
              (allocation.docNumber ?? '').toLowerCase().includes(query)
            )
        );
        return {
          ...entry,
          records,
          visibleTotal: records.reduce((sum, record) => sum + record.amount, 0),
        };
      })
      .filter((entry) => entry.records.length > 0);
  }, [entries, search]);

  /**
   * Grouped by the credit whose deposit covers them.
   *
   * Keyed on the bank credit first and the ERP deposit second: `erpDepositId` is the truest key but
   * only exists once the deposit has been made, and a credit still waiting on one of its receipts
   * has to be groupable too — that is precisely the state someone opens this tab to understand.
   *
   * A credit with one receipt is not wrapped. The receipt band already names the credit and owns the
   * deposit answer, and a second frame around it would add nothing.
   */
  const { creditGroups, looseEntries } = useMemo(() => {
    const byCredit = new Map<string, ReceiptWithRecords[]>();
    const loose: ReceiptWithRecords[] = [];

    for (const entry of visibleEntries) {
      const key = entry.receipt.depositBankRecordId || entry.receipt.erpDepositId;
      if (!key) {
        loose.push(entry);
        continue;
      }
      byCredit.set(key, [...(byCredit.get(key) ?? []), entry]);
    }

    const groups: CreditGroup[] = [];
    for (const [key, groupEntries] of byCredit) {
      if (groupEntries.length < 2) {
        loose.push(...groupEntries);
        continue;
      }

      const receipts = groupEntries.map((entry) => entry.receipt);
      const bankRecordId = receipts.find((r) => r.depositBankRecordId)?.depositBankRecordId;
      const deposit = bankTransactions.find((txn) => txn.id === bankRecordId) ?? null;
      const status = creditDepositStatus(receipts);

      groups.push({
        key,
        deposit,
        erpDepositId: receipts.find((r) => r.erpDepositId)?.erpDepositId ?? null,
        entries: groupEntries,
        total: receipts.reduce((sum, receipt) => sum + receipt.amount, 0),
        paymentCount: receipts.reduce((sum, receipt) => sum + receipt.remittanceIds.length, 0),
        status,
        /**
         * The first receipt with something to say. `depositWaitingReason` is why this carries a
         * sentence at all: a receipt waiting on the *other* receipt on its credit would otherwise
         * get the badge's stock tooltip, which says the bank credit has not arrived — both wrong
         * and unactionable.
         */
        statusTitle:
          receipts.find((r) => r.depositError)?.depositError ??
          receipts.find((r) => r.depositWaitingReason)?.depositWaitingReason ??
          null,
        // One retry for the credit, on the same rule a single receipt uses. Either receipt can carry
        // it: the job resolves the credit and deposits the whole thing.
        recoverable:
          status !== 'SUCCESSFUL' &&
          receipts.some((r) => r.depositStatus === 'FAILED' || isDepositAbandoned(r)),
        unaccounted: deposit ? depositUnaccountedAmount(deposit) : 0,
      });
    }

    groups.sort((a, b) => (b.deposit?.date ?? '').localeCompare(a.deposit?.date ?? ''));

    return { creditGroups: groups, looseEntries: loose };
  }, [visibleEntries, bankTransactions]);

  /**
   * Retry the payments on a receipt that did not post.
   *
   * Only the failed ones are re-attempted, and that is the server's doing rather than a selection
   * made here — each record's stored payment ids make its two money legs skip. Duplicating the
   * choice on this side is how the two would drift.
   */
  const handleRetryPost = useCallback(
    async (receipt: CashReceipt) => {
      if (!supplierId) return;
      const result = await dispatch(retryPostThunk({ supplierId, receiptId: receipt.id }));
      if (retryPostThunk.rejected.match(result)) {
        toast.error(`${result.payload ?? 'Could not retry the post'}`, { autoClose: 5000 });
      }
      dispatch(fetchCashReceiptsThunk({ supplierId }));
      dispatch(fetchRemittanceRecordsThunk({ supplierId }));
    },
    [dispatch, supplierId]
  );

  /**
   * Retry a deposit from any receipt it covers.
   *
   * The endpoint resolves the credit and deposits everything on it, so one press is the right unit
   * — and it is why the button lives on the band rather than on each payment, where an operator
   * would otherwise press it five times for one deposit.
   */
  const handleRetryDeposit = useCallback(
    async (receipts: CashReceipt[]) => {
      if (!supplierId) return;
      const candidate = receipts.find((r) => r.depositStatus === 'FAILED') ?? receipts[0];
      const result = await dispatch(retryDepositThunk({ supplierId, receiptId: candidate.id }));
      if (retryDepositThunk.rejected.match(result)) {
        toast.error(`${result.payload ?? 'Could not retry the deposit'}`, { autoClose: 5000 });
      }
      dispatch(fetchCashReceiptsThunk({ supplierId }));
      dispatch(fetchBankTransactionsThunk({ supplierId }));
    },
    [dispatch, supplierId]
  );

  /** One payment, in the row shape both the loose and the grouped bands use. */
  const renderRecordRow = (record: RemittanceRecord, receipt: CashReceipt) => {
    const unapplied = recordUnappliedAmount(record);
    const status = record.erpPostStatus ?? receipt.postStatus;

    return (
      <button
        type="button"
        key={record.id}
        onClick={() => setDetailId(receipt.id)}
        className="flex items-start w-full gap-3 px-3 py-2 pl-8 text-left transition-colors hover:bg-gray-50">
        <span className="w-20 text-xs text-gray-500 shrink-0">
          {formatDate(record.paymentDate)}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-medium text-gray-900 truncate">
            {record.payerName ?? 'Unknown payer'}
            {record.checkNumber && (
              <span className="ml-1.5 font-normal text-gray-400">#{record.checkNumber}</span>
            )}
          </span>
          {/* This payment's own reason, under the payer it belongs to. A receipt-level failure is
              shown once at the top of the band instead. */}
          {record.erpPostError && (
            <span className="block text-[11px] text-red-600 truncate" title={record.erpPostError}>
              {record.erpPostError}
            </span>
          )}
        </span>
        <span className="w-24 text-xs font-semibold text-right text-gray-900 tabular-nums">
          {formatCurrency(record.amount)}
        </span>
        <span className="flex flex-wrap w-52 gap-1 shrink-0">
          {record.appliedInvoices.map((allocation) => (
            <span
              key={allocation.invoiceId}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
              {allocation.docNumber}
            </span>
          ))}
          {/* Beside the invoices, or the row looks like it applied less than the payment carried. */}
          {unapplied > 0.01 && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"
              title="Posted as open credit on the customer's account">
              {formatCurrency(unapplied)} credit
            </span>
          )}
          {recordWasAdjusted(record) && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
              Edited
            </span>
          )}
        </span>
        <PostStatusBadge status={status} title={record.erpPostError} />
      </button>
    );
  };

  /** A payment the receipt names but the store has not loaded. Never silently dropped. */
  const renderMissingRow = (id: string) => (
    <div key={id} className="flex items-center gap-3 px-3 py-2 pl-8">
      <span className="text-xs italic text-gray-400">Payment details not loaded</span>
      <span className="font-mono text-[10px] text-gray-300">{id.slice(0, 8)}</span>
    </div>
  );

  const renderReceiptBand = (entry: ReceiptWithRecords, showDeposit: boolean) => {
    const { receipt } = entry;
    const retriable = receipt.postStatus === 'FAILED' || receipt.postStatus === 'PARTIAL';
    const depositRecoverable = receipt.depositStatus === 'FAILED' || isDepositAbandoned(receipt);

    return (
      <ReceiptBand
        key={receipt.id}
        receipt={receipt}
        deposit={bankTransactions.find((txn) => txn.id === receipt.depositBankRecordId) ?? null}
        recordCount={receipt.remittanceIds.length}
        postedCount={entry.postedCount}
        showDeposit={showDeposit}
        visibleTotal={search.trim() ? undefined : entry.visibleTotal}
        expanded={!collapsedKeys.has(receipt.id)}
        onToggle={() => toggleGroup(receipt.id)}
        status={
          <>
            <PostMethodBadge method={receipt.postMethod} />
            <DepositStatusBadge
              status={receipt.depositStatus}
              title={receipt.depositError ?? receipt.depositWaitingReason}
            />
          </>
        }
        action={
          retriable ? (
            <DillButton
              onClick={() => handleRetryPost(receipt)}
              label={receipt.postStatus === 'PARTIAL' ? 'Retry failed payments' : 'Retry post'}
              variant="ghost-yellow"
              icon={<ArrowClockwise size={13} weight="bold" />}
            />
          ) : showDeposit && depositRecoverable ? (
            <DillButton
              onClick={() => handleRetryDeposit([receipt])}
              label="Retry deposit"
              variant="ghost-yellow"
              icon={<ArrowClockwise size={13} weight="bold" />}
            />
          ) : undefined
        }>
        {entry.records.map((record) => renderRecordRow(record, receipt))}
        {entry.missingIds.map(renderMissingRow)}
      </ReceiptBand>
    );
  };

  // Bands, not rows, so the limit counts bands. A credit group counts as one.
  const shown = [
    ...creditGroups.map((group) => ({ kind: 'credit' as const, group })),
    ...looseEntries.map((entry) => ({ kind: 'receipt' as const, entry })),
  ];
  const visible = shown.slice(0, visibleLimit);

  return (
    <div className="flex flex-col h-full gap-4 px-4 py-4 md:px-6">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          {
            key: 'totalPosted',
            label: 'Total Posted',
            value: formatCurrency(totalAmount),
            icon: <CheckCircle size={16} weight="fill" className="text-emerald-600" />,
          },
          {
            key: 'successful',
            label: 'Successful',
            value: successfulCount.toString(),
            icon: <CheckCircle size={16} weight="fill" className="text-blue-500" />,
          },
          {
            key: 'pending',
            label: 'Pending',
            value: pendingCount.toString(),
            icon: <Clock size={16} weight="fill" className="text-amber-500" />,
          },
          {
            key: 'failed',
            label: partialCount > 0 ? 'Failed / partial' : 'Failed',
            value: (failedCount + partialCount).toString(),
            icon: <XCircle size={16} weight="fill" className="text-red-500" />,
          },
        ].map((card) => (
          <div key={card.key} className="p-3 bg-white border border-gray-100 rounded-xl">
            <div className="flex items-center gap-2">
              {card.icon}
              <span className="text-xs text-gray-500">{card.label}</span>
            </div>
            <p className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex gap-0.5 p-0.5 bg-gray-100 rounded-lg">
            {[
              { key: 'all', label: 'All', count: null },
              { key: 'SUCCESSFUL', label: 'Successful', count: successfulCount },
              { key: 'PENDING', label: 'Pending', count: pendingCount },
              // Shown only when there is one, like Failed — a permanent zero is noise.
              ...(partialCount > 0
                ? [{ key: 'PARTIAL', label: 'Partly posted', count: partialCount }]
                : []),
              ...(failedCount > 0 ? [{ key: 'FAILED', label: 'Failed', count: failedCount }] : []),
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all ' +
                  (statusFilter === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700')
                }>
                {tab.label}
                {tab.count !== null && (
                  <span
                    className={
                      'ml-1.5 tabular-nums ' +
                      (statusFilter === tab.key ? 'text-gray-400' : 'text-gray-300')
                    }>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/*
            Beside the Pending tab, because that tab's count is the reason to press it. Posting
            hands the accounting call to a background worker, so a row reads Pending for a few
            seconds after the request returned, and nothing else on screen says the answer is
            still coming.
          */}
          <span
            title={
              pendingCount > 0
                ? `${pendingCount} post${
                    pendingCount === 1 ? '' : 's'
                  } still with the ERP — refresh to see the outcome`
                : 'Re-read posts, payments and deposits'
            }>
            <DillButton
              onClick={handleRefresh}
              label="Refresh"
              variant="secondary"
              height={30}
              isLoading={loading}
              icon={<ArrowClockwise size={13} weight="bold" />}
            />
          </span>
        </div>

        <div className="relative w-64">
          <MagnifyingGlass
            size={14}
            className="absolute -translate-y-1/2 left-3 top-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search payer, invoice or deposit..."
            className="w-full py-2 pl-8 pr-3 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-3 overflow-y-auto border border-gray-100 rounded-xl bg-gray-50">
        {visible.length === 0 ? (
          <p className="py-10 text-sm text-center text-gray-400">
            {loading ? 'Loading...' : 'No matching posts found.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {visible.map((item) =>
              item.kind === 'receipt' ? (
                renderReceiptBand(item.entry, true)
              ) : (
                /*
                  Two receipts on one bank credit. The credit-level band owns the deposit — it is
                  one document covering both — so the receipts inside it show their post status only.
                */
                <DepositGroupBand
                  key={item.group.key}
                  deposit={item.group.deposit}
                  erpDepositId={item.group.erpDepositId}
                  paymentCount={item.group.paymentCount}
                  paymentTotal={item.group.total}
                  unaccounted={item.group.unaccounted}
                  expanded={!collapsedKeys.has(item.group.key)}
                  onToggle={() => toggleGroup(item.group.key)}
                  status={
                    <DepositStatusBadge status={item.group.status} title={item.group.statusTitle} />
                  }
                  action={
                    item.group.recoverable ? (
                      <DillButton
                        onClick={() =>
                          handleRetryDeposit(item.group.entries.map((entry) => entry.receipt))
                        }
                        label="Retry deposit"
                        variant="ghost-yellow"
                        icon={<ArrowClockwise size={13} weight="bold" />}
                      />
                    ) : undefined
                  }>
                  <div className="flex flex-col gap-2 p-2 bg-gray-50">
                    {item.group.entries.map((entry) => renderReceiptBand(entry, false))}
                  </div>
                </DepositGroupBand>
              )
            )}

            {shown.length > visible.length && (
              <button
                type="button"
                onClick={() => setVisibleLimit((limit) => limit + PAGE_SIZE)}
                className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                <WarningCircle size={13} />
                Show more ({shown.length - visible.length} remaining)
              </button>
            )}
          </div>
        )}
      </div>

      {detailItem && <ReceiptDetailModal receipt={detailItem} onClose={() => setDetailId(null)} />}
    </div>
  );
};
